/**
 * Higher-order component for providing feature flags to the app
 * @module lib/feature-flags/withFeatureFlagProvider
 */

import React, { useState, useEffect } from "react";
import initializeFeatures from "./initializeFeatures";
import FeatureFlagContext from "./FeatureFlagContext";
import { withAuthProvider } from "../auth";
import { LoadingComponent } from "@pga/pga-component-library";

/**
 * Internal provider component that initializes feature flags
 * Wrapped with withAuthProvider to access user data
 */
const FeatureFlagProvider = withAuthProvider((props) => {
  const [featureFlags, setFeatureFlags] = useState();
  const { App, me } = props;

  // Extract user identification from auth provider's me object
  const universalId = me?.universalId;
  const primaryEmail = me?.primaryEmail;

  useEffect(() => {
    const getFeatures = async () => {
      const flags = await initializeFeatures({ universalId, primaryEmail });
      setFeatureFlags(flags);
    };
    getFeatures();
  }, [universalId, primaryEmail]);

  // Show loading state while flags are being initialized
  if (featureFlags === undefined) {
    return <LoadingComponent />;
  }

  return (
    <FeatureFlagContext.Provider value={featureFlags}>
      <App {...props} />
    </FeatureFlagContext.Provider>
  );
});

/**
 * Higher-order component that wraps an app component with feature flag provider
 *
 * @param {React.ComponentType} App - The app component to wrap
 * @returns {React.ComponentType} Wrapped component with feature flag context
 *
 * @example
 * // In App.js
 * import { withFeatureFlagProvider } from '../lib/feature-flags';
 *
 * const App = () => (
 *   <ThemeProvider theme={theme}>
 *     // ... app content
 *   </ThemeProvider>
 * );
 *
 * export default withFeatureFlagProvider(App);
 */
const withFeatureFlagProvider = (App) => (props) => {
  return <FeatureFlagProvider App={App} {...props} />;
};

export default withFeatureFlagProvider;
